import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';

const modules = [
  {
    name: 'Signal Architecture',
    description: 'Define what engagement looks like for your product.',
    fields: ['Primary engagement signal', 'Value milestone indicators', 'Warning signal triggers'],
  },
  {
    name: 'Risk Categories',
    description: 'Group accounts by behavior patterns and priority.',
    fields: ['Active risk categories', 'Category thresholds', 'Escalation criteria'],
  },
  {
    name: 'Intervention Playbooks',
    description: 'Specific actions for each risk level.',
    fields: ['Primary churn driver', 'Intervention timing', 'Success criteria'],
  },
  {
    name: 'Leading Metrics',
    description: 'Numbers that predict outcomes before renewal.',
    fields: ['Stakeholder signal', 'Adoption benchmarks', 'Health indicators'],
  },
  {
    name: 'Renewal Process',
    description: 'End-to-end workflow from signal to signature.',
    fields: ['Renewal timeline', 'Responsibility matrix', 'Documentation requirements'],
  },
];

const faqs = [
  {
    question: 'Who is this for?',
    answer: 'Operators at Series A+ SaaS companies who are responsible for retention outcomes. Founders, heads of customer success, revenue operations leaders. People who want a structured system instead of scattered intuition.',
  },
  {
    question: 'How long does it take?',
    answer: 'Most operators complete all five modules in 2 to 4 hours spread across multiple sessions. You can save progress and return anytime. The AI picks up where you left off.',
  },
  {
    question: 'What do I need to know before starting?',
    answer: 'You should understand your product, your customers, and your current retention challenges. The AI asks questions about your specific business. Generic answers produce generic output. The more specific you are, the more useful the result.',
  },
  {
    question: 'How is this different from just asking ChatGPT?',
    answer: 'The Retention Builder uses a structured framework based on operational retention systems we have built. It asks the right questions in the right order, extracts structured data, and produces a documented system. ChatGPT gives you prose. This gives you a working framework.',
  },
  {
    question: 'What happens to my data?',
    answer: 'Your data stays private. We do not train models on your responses. We do not share your information. You can export your completed system and delete your account at any time.',
  },
];

export function ToolPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="section-padding bg-slate-50 dark:bg-obsidian">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-black text-obsidian dark:text-signal leading-tight">
              Build your retention system.{' '}
              <span className="text-amber">Five modules. AI-guided.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Answer the AI's questions about your business. It builds your retention system documentation as you go.
            </p>
            <div className="mt-10">
              <a href="https://app.churnlogix.com">
                <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                  Start building
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: What you get */}
      <section className="section-padding bg-white dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal text-center mb-12">
            What you get
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <Card key={module.name} variant="bordered" padding="lg">
                <CardContent>
                  <span className="text-xs font-semibold text-amber uppercase tracking-wider">
                    Module {index + 1}
                  </span>
                  <h3 className="mt-2 text-lg font-heading font-black text-obsidian dark:text-signal">
                    {module.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    {module.description}
                  </p>
                  <div className="mt-4 space-y-2">
                    {module.fields.map((field) => (
                      <div key={field} className="flex items-center gap-2 text-xs text-slate-500">
                        <div className="w-1 h-1 rounded-full bg-amber" />
                        {field}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: How it works */}
      <section className="section-padding bg-slate-100 dark:bg-[#0F1014]">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal text-center mb-12">
            How it works
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded bg-amber/10 border border-amber/20 flex items-center justify-center mx-auto">
                <span className="text-2xl font-heading font-black text-amber">1</span>
              </div>
              <h3 className="mt-4 text-lg font-heading font-black text-obsidian dark:text-signal">
                Answer questions
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                The AI asks specific questions about your business. Not generic prompts. Questions calibrated to your product type and churn rate.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded bg-amber/10 border border-amber/20 flex items-center justify-center mx-auto">
                <span className="text-2xl font-heading font-black text-amber">2</span>
              </div>
              <h3 className="mt-4 text-lg font-heading font-black text-obsidian dark:text-signal">
                Your system builds
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                As you answer, the AI extracts structured information and populates your retention system document in real time.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded bg-amber/10 border border-amber/20 flex items-center justify-center mx-auto">
                <span className="text-2xl font-heading font-black text-amber">3</span>
              </div>
              <h3 className="mt-4 text-lg font-heading font-black text-obsidian dark:text-signal">
                Export and use
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                When all five modules are complete, export your documented system as a text file you can share with your team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Pricing */}
      <section className="section-padding bg-white dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal text-center mb-12">
            Pricing
          </h2>

          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Card variant="bordered" padding="lg">
              <CardContent>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Monthly
                </span>
                <p className="mt-2 text-4xl font-heading font-black text-obsidian dark:text-signal">
                  $49<span className="text-lg text-slate-500">/mo</span>
                </p>
                <div className="mt-6 space-y-3 text-sm text-slate-600 dark:text-slate-400">
                  <p>All five modules</p>
                  <p>AI-guided conversation</p>
                  <p>Exportable system document</p>
                  <p>Access to future modules</p>
                </div>
                <div className="mt-6">
                  <a href="https://app.churnlogix.com">
                    <Button variant="outline" size="sm" className="w-full">
                      Start building
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered" padding="lg" className="border-amber">
              <CardContent>
                <span className="text-xs font-semibold text-amber uppercase tracking-wider">
                  Annual
                </span>
                <p className="mt-2 text-4xl font-heading font-black text-obsidian dark:text-signal">
                  $399<span className="text-lg text-slate-500">/yr</span>
                </p>
                <p className="text-sm text-amber mt-1">Save $189</p>
                <div className="mt-6 space-y-3 text-sm text-slate-600 dark:text-slate-400">
                  <p>Everything in monthly</p>
                  <p>Priority support</p>
                  <p>Early access to new features</p>
                  <p>Two months free</p>
                </div>
                <div className="mt-6">
                  <a href="https://app.churnlogix.com">
                    <Button variant="primary" size="sm" className="w-full">
                      Start building
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 4: FAQ */}
      <section className="section-padding bg-slate-100 dark:bg-[#0F1014]">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal text-center mb-12">
            Questions
          </h2>

          <div className="max-w-3xl mx-auto space-y-8">
            {faqs.map((faq) => (
              <div key={faq.question}>
                <h3 className="text-lg font-heading font-black text-obsidian dark:text-signal">
                  {faq.question}
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-400 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Final CTA */}
      <section className="section-padding bg-white dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
            Start building your retention system
          </h2>
          <div className="mt-8">
            <a href="https://app.churnlogix.com">
              <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                Start building
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ToolPage;
